import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import { Typography } from "antd";
import * as XLSX from "xlsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle } from "@fortawesome/free-regular-svg-icons";
import { list } from "../../../../controllers/V1/invoice/invoiceControllers";
import Loading from "../../../../components/Loading/LoadingEffect";
import { Link } from "react-router-dom";
import moment from "moment";

const ExportPopup = ({ popUpOpen, togglePopUp }) => {
  const handleCancel = () => {
    togglePopUp();
    setIsLoading(false);
    setError(null);
  };

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [helpText, setHelpText] = useState("");

  const handleSearchByChange = (e) => {
    const { value } = e.target;
    if (value === "invoice_date") {
      setHelpText("Format should be YYYY-MM-DD. (e:g 2024-12-25)");
    } else if (value === "payment_mode") {
      setHelpText("Enter Cash/Online only.");
    } else {
      setHelpText("");
    }
  };

  const buildCondition = (formData) => {
    const filterBy = formData.get("filter_by") || "";
    const search = formData.get("search") || "";
    const from = formData.get("from_date") || "";
    const to = formData.get("to_date") || "";
    const status = formData.get("status") || "";

    return {
      ...(filterBy === "users_name" && search
        ? { users_name: { $regex: search, $options: "i" } }
        : null),
      ...(filterBy === "store" && search
        ? { store: { $regex: search, $options: "i" } }
        : null),
      ...(filterBy === "users_mobile" && search
        ? { users_mobile: parseInt(search) }
        : null),
      ...(filterBy === "pos_number" && search
        ? { pos_number: parseInt(search) }
        : null),
      ...(filterBy === "invoice_no" && search
        ? { invoice_no: parseInt(search) }
        : null),
      ...(filterBy === "bind_person_name" && search
        ? { bind_person_name: { $regex: search, $options: "i" } }
        : null),
      ...(filterBy === "bind_person_mobile" && search
        ? { bind_person_mobile: parseInt(search) }
        : null),
      ...(filterBy === "payment_mode" && search
        ? { payment_mode: { $regex: search, $options: "i" } }
        : null),
      ...(filterBy === "batch_no" && search
        ? { batch_no: parseInt(search) }
        : null),
      ...(filterBy === "sub_total_due" && search
        ? { sub_total_due: parseFloat(search) }
        : null),
      ...(filterBy === "invoice_date" && search
        ? {
            invoice_date: {
              $gte: new Date(`${search} 0:01:00`),
              $lte: new Date(`${search} 23:59:00`),
            },
          }
        : null),
      ...(status ? { status } : null),
      ...(from ? { createdAt: {$gte : `${from} 00:00:00`} } : null),
      ...(to ? { createdAt: {$lte : `${to} 23:59:59`} } : null),
    };
  };

  const formatPeriod = (date, time) => {
    if (!date) return "";
    const formattedDate = moment(date).format("DD-MM-YYYY");
    const formattedTime = time
      ? moment(`2025-03-03 ${time}`).format("hh:mm A")
      : "";
    return `${formattedDate} ${formattedTime}`.trim();
  };

  const handleExport = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      setIsLoading(true);
      const formData = new FormData(e.target);
      const option = {
        isExport: true,
        condition: buildCondition(formData),
        select: {},
        sort: { _id: -1 },
      };

      const res = await list(option);
      if (res.status === true && res?.result?.length > 0) {
        const modifiedData = res.result.map((item, index) => ({
          "Sl. No.": index + 1,
          "Invoice No.": item?.invoice_no ?? "",
          "POS Number": item?.pos_number ?? "",
          Store: item?.store ?? "",
          "Customer Name": item?.users_name ?? "",
          Mobile: item?.users_mobile ?? "",
          "Bind With": item?.bind_person_name ?? "",
          "Bind Mobile": item?.bind_person_mobile ?? "",
          Amount: item?.total_due != null ? Number(item.total_due).toFixed(2) : "",
          "Total Sales": item?.total_sales != null ? Number(item.total_sales).toFixed(2) : "",
          "Total Commission": item?.total_commission != null ? Number(item.total_commission).toFixed(2) : "",
          "Total Paid": item?.total_paid != null ? Number(item.total_paid).toFixed(2) : "",
          "Sub Total Due": item?.sub_total_due != null ? Number(item.sub_total_due).toFixed(2) : "",
          // "Payment Mode": item?.payment_mode ?? "",
          "Period From": formatPeriod(item?.start_date, item?.start_time),
          "Period To": formatPeriod(item?.end_date, item?.end_time),
          // "Due Date": item?.due_date
            // ? moment(item.due_date).format("DD-MM-YYYY")
            // : "",
          "Invoice Date": item?.invoice_date
            ? moment(item.invoice_date).format("DD-MM-YYYY")
            : "",
          Status: item?.status ?? "",
        }));
        await exportToExcel(modifiedData);
        handleCancel();
      } else {
        setIsLoading(false);
        setError("No records found matching the criteria.");
      }
    } catch (err) {
      setIsLoading(false);
      setError("An error occurred while exporting the data.");
      console.error("Export Error:", err);
    }
  };

  const exportToExcel = async (data) => {
    try {
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Invoices");
      XLSX.writeFile(
        workbook,
        `B-Winn Individual Invoices ${moment().format("YYYY-MM-DD")}.xlsx`
      );
    } catch (err) {
      alert("Error generating Excel file: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={popUpOpen}
      onClose={handleCancel}
      fullWidth={true}
      maxWidth={"md"}
      PaperProps={{
        className: "myDialogPopUp",
      }}
    >
      <div className="myDrawer">
        <div className="inventory_popup_title">
          <Typography
            variant="h5"
            component="div"
            gutterBottom
            className="inventory_popup_title_heading"
          >
            Export Individual Invoices
          </Typography>
          <Link
            to={false}
            style={{ position: "absolute", right: 8, top: 8, color: "#dd5151" }}
            onClick={handleCancel}
          >
            <FontAwesomeIcon icon={faTimesCircle} />
          </Link>
        </div>
        <Typography
          variant="body1"
          component="div"
          className="my_delete_popup_paragraph"
        >
          <div className="container-fluid">
            <div className="categories_open_popup_main_contnet">
              <form onSubmit={handleExport}>
                <div className="form-group row">
                  <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                    <label htmlFor="filter_by" className="all_lable_for_vendor_dashbaord">
                      Search Field :
                    </label>
                    <select
                      name="filter_by"
                      id="filter_by"
                      className="form-control categories_select_tg_enter_product"
                      onChange={handleSearchByChange}
                    >
                      <option value="">Select search field</option>
                      <option value="users_name">Name</option>
                      <option value="users_mobile">Mobile</option>
                      <option value="store">Store</option>
                      <option value="pos_number">POS Number</option>
                      <option value="bind_person_name">Sales Person Name</option>
                      <option value="bind_person_mobile">Sales Person Mobile</option>
                      <option value="payment_mode">Payment</option>
                      <option value="batch_no">Batch Number</option>
                      <option value="invoice_no">Invoice Number</option>
                      <option value="invoice_date">Invoice Date</option>
                      <option value="sub_total_due">Amount</option>
                    </select>
                  </div>
                  <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                    <label htmlFor="search" className="all_lable_for_vendor_dashbaord">
                      Search Text :
                    </label>
                    <input
                      type="text"
                      name="search"
                      id="search"
                      placeholder="Enter search text"
                      className="form-control categories_select_tg_enter_product"
                    />
                    {helpText ? (
                      <label style={{ color: "blue" }}>{helpText}</label>
                    ) : (
                      ""
                    )}
                  </div>
                  <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                    <label htmlFor="from_date" className="all_lable_for_vendor_dashbaord">
                      From :
                    </label>
                    <input
                      type="date"
                      name="from_date"
                      id="from_date"
                      placeholder="YYYY-MM-DD"
                      className="form-control categories_select_tg_enter_product"
                    />
                  </div>
                  <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                    <label htmlFor="to_date" className="all_lable_for_vendor_dashbaord">
                      To :
                    </label>
                    <input
                      type="date"
                      name="to_date"
                      id="to_date"
                      placeholder="YYYY-MM-DD"
                      className="form-control categories_select_tg_enter_product"
                    />
                  </div>
                  <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                    <label htmlFor="status" className="all_lable_for_vendor_dashbaord">
                      Status :
                    </label>
                    <select
                      name="status"
                      id="status"
                      className="form-control categories_select_tg_enter_product"
                    >
                      <option value="">All</option>
                      <option value="Completed">Completed</option>
                      <option value="Pending">Pending</option>
                    </select>
                  </div>
                  {error ? (
                    <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                      <label style={{ color: "red" }}>{error}</label>
                    </div>
                  ) : null}
                  <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                    <div className="inventory_open_popup_action_btn">
                      <button
                        type="button"
                        className="btn btn-light categories_cancel_button"
                        onClick={handleCancel}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-danger" disabled={isLoading}>
                        Download Excel
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </Typography>
      </div>
      <Loading isLoading={isLoading} text="Preparing download..." />
    </Dialog>
  );
};

export default ExportPopup;
